import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Play, 
  Clock, 
  Calendar, 
  User, 
  ExternalLink,
  FileText,
  Globe,
  Youtube,
  Music,
  Linkedin,
  Loader2,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Link as LinkIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useVideo, useVideos } from '../hooks/useContent';

// Platform icon mapping
const platformIcons = {
  spotify: Music,
  apple: Music,
  youtube: Youtube,
  google: Globe,
  globe: Globe,
  linkedin: Linkedin,
  instagram: Globe,
};

// Helper to extract YouTube video ID
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

// Generate thumbnail URL from video URL
const generateThumbnail = (videoUrl, customThumbnail) => {
  if (customThumbnail) return customThumbnail;
  
  // Try to extract YouTube thumbnail
  const youtubeId = getYouTubeVideoId(videoUrl);
  if (youtubeId) {
    return `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`;
  }
  
  return null;
};

export default function VideoDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [currentClipIndex, setCurrentClipIndex] = useState(0);
  
  // Fetch the specific video from Contentful (with static fallback)
  const { video, isLoading, error } = useVideo(slug);
  // Fetch all videos for related videos
  const { videos: allVideos } = useVideos();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-secondary" />
        <span className="ml-2 text-slate-600">Loading video...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold text-brand-primary mb-4">
            Error Loading Video
          </h1>
          <p className="text-slate-600 mb-8">{error}</p>
          <Button
            asChild
            className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90"
          >
            <Link to="/videos">Back to Videos</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="video-not-found">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold text-brand-primary mb-4">
            Video Not Found
          </h1>
          <p className="text-slate-600 mb-8">
            The video you&apos;re looking for doesn&apos;t exist or has been moved.
          </p>
          <Button
            asChild
            className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90"
          >
            <Link to="/videos">Back to Videos</Link>
          </Button>
        </div>
      </div>
    );
  }

  const relatedVideos = allVideos
    .filter((v) => v.id !== video.id && v.category === video.category)
    .slice(0, 3);

  // Process clips to add generated thumbnails
  const clips = (video.clips || []).map(clip => ({
    ...clip,
    thumbnail: generateThumbnail(clip.url, clip.thumbnail),
  }));
  const hasClips = clips.length > 0;

  const nextClip = () => {
    setCurrentClipIndex((prev) => (prev + 1) % clips.length);
  };

  const prevClip = () => {
    setCurrentClipIndex((prev) => (prev - 1 + clips.length) % clips.length);
  };

  return (
    <div data-testid="video-detail-page">
      {/* Hero Section with Video */}
      <section className="py-8 md:py-12 bg-brand-primary">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center text-white/70 hover:text-white mb-6 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </button>

            {/* Video Embed */}
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                src={video.embedUrl}
                title={video.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Info Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                {/* Category Badge */}
                <span className="inline-block text-brand-secondary text-xs font-semibold uppercase tracking-wider bg-brand-secondary/10 px-4 py-1.5 rounded-full mb-4">
                  {video.category}
                </span>

                {/* Title */}
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-brand-primary mb-4">
                  {video.title}
                </h1>

                {/* Meta Info */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-6">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {video.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {video.duration}
                  </span>
                </div>

                {/* Description */}
                <div className="prose prose-lg max-w-none mb-8">
                  <p className="text-slate-600 leading-relaxed">
                    {video.fullDescription}
                  </p>
                </div>

                {/* Featured Guest Section - Image at top, full width */}
                {video.guestName && (
                  <div className="bg-brand-accent rounded-2xl overflow-hidden mb-8">
                    {/* Guest Image - Full width at top */}
                    {video.guestImage && (
                      <div className="w-full">
                        <img
                          src={video.guestImage}
                          alt={video.guestName}
                          className="w-full h-64 md:h-80 object-cover object-top"
                        />
                      </div>
                    )}
                    
                    {/* Guest Info */}
                    <div className="p-6">
                      <h3 className="font-heading text-xl font-bold text-brand-primary mb-2">
                        Featured Guest
                      </h3>
                      <p className="font-heading text-2xl font-bold text-brand-primary">{video.guestName}</p>
                      <p className="text-brand-secondary font-medium mb-3">{video.guestTitle}</p>
                      <p className="text-slate-600 leading-relaxed">{video.guestBio}</p>
                      
                      {/* Guest Resources/Books */}
                      {video.guestResources && video.guestResources.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <p className="font-semibold text-brand-primary mb-2 flex items-center gap-2">
                            <BookOpen className="w-4 h-4 text-brand-secondary" />
                            Resources & Books
                          </p>
                          <div className="space-y-2">
                            {video.guestResources.map((resource, idx) => (
                              <a
                                key={idx}
                                href={resource.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-brand-secondary hover:underline text-sm"
                              >
                                <LinkIcon className="w-3 h-3" />
                                {resource.title}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Clips Carousel Section - Card Style like Mel Robbins */}
                {hasClips && (
                  <div className="mb-8">
                    <h3 className="font-heading text-xl font-bold text-brand-primary mb-4">
                      Episode Clips
                    </h3>
                    
                    {/* Clips Grid - Card Style */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {clips.map((clip, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className={`group bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden ${
                            idx === currentClipIndex ? 'ring-2 ring-brand-secondary' : ''
                          }`}
                        >
                          {/* Clip Thumbnail */}
                          <a
                            href={clip.url || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block relative aspect-video"
                            onClick={(e) => {
                              if (!clip.url) {
                                e.preventDefault();
                                setCurrentClipIndex(idx);
                              }
                            }}
                          >
                            {clip.thumbnail ? (
                              <>
                                <img
                                  src={clip.thumbnail}
                                  alt={clip.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  <div className="w-12 h-12 rounded-full bg-brand-secondary flex items-center justify-center">
                                    <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="w-full h-full bg-brand-accent flex items-center justify-center">
                                <Play className="w-10 h-10 text-brand-secondary" />
                              </div>
                            )}
                            
                            {/* Duration Badge */}
                            {clip.duration && (
                              <span className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 text-white text-xs rounded">
                                {clip.duration}
                              </span>
                            )}
                          </a>
                          
                          {/* Clip Info */}
                          <div className="p-4">
                            <h4 className="font-semibold text-brand-primary text-sm line-clamp-2 mb-1 group-hover:text-brand-secondary transition-colors">
                              {clip.title}
                            </h4>
                            {clip.timestamp && (
                              <p className="text-slate-500 text-xs">
                                Timestamp: {clip.timestamp}
                              </p>
                            )}
                            {clip.url && (
                              <a
                                href={clip.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 mt-2 text-brand-secondary hover:underline text-xs font-medium"
                              >
                                <Play className="w-3 h-3" />
                                Watch Clip
                              </a>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                    
                    {/* Navigation for many clips */}
                    {clips.length > 6 && (
                      <div className="flex items-center justify-center gap-4 mt-6">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={prevClip}
                          className="rounded-full"
                        >
                          <ChevronLeft className="w-4 h-4 mr-1" />
                          Previous
                        </Button>
                        <span className="text-sm text-slate-500">
                          {currentClipIndex + 1} of {clips.length}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={nextClip}
                          className="rounded-full"
                        >
                          Next
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Transcript Section */}
                {video.transcript && (
                  <div className="mb-8">
                    <h3 className="font-heading text-xl font-bold text-brand-primary mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-brand-secondary" />
                      Transcript
                    </h3>
                    <div className="bg-slate-50 rounded-2xl p-6">
                      <div className="prose prose-sm max-w-none text-slate-600">
                        {video.transcript.split('\n\n').map((paragraph, idx) => (
                          <p key={idx} className="mb-4">{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="sticky top-24"
              >
                {/* Listen/Watch Platforms */}
                {video.platforms && video.platforms.length > 0 && (
                  <div className="bg-brand-accent rounded-2xl p-6 mb-6">
                    <h3 className="font-heading text-lg font-bold text-brand-primary mb-4">
                      Listen & Watch
                    </h3>
                    <p className="text-slate-600 text-sm mb-4">
                      Available on these platforms:
                    </p>
                    <div className="space-y-3">
                      {video.platforms.map((platform, index) => {
                        const IconComponent = platformIcons[platform.icon] || Globe;
                        return (
                          <a
                            key={index}
                            href={platform.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 bg-white rounded-xl hover:shadow-md transition-shadow"
                          >
                            <div className="w-10 h-10 rounded-full bg-brand-secondary/10 flex items-center justify-center">
                              <IconComponent className="w-5 h-5 text-brand-secondary" />
                            </div>
                            <span className="font-medium text-brand-primary">{platform.name}</span>
                            <ExternalLink className="w-4 h-4 text-slate-400 ml-auto" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Related Videos */}
                {relatedVideos.length > 0 && (
                  <div className="bg-brand-accent rounded-2xl p-6">
                    <h3 className="font-heading text-lg font-bold text-brand-primary mb-4">
                      Related Episodes
                    </h3>
                    <div className="space-y-4">
                      {relatedVideos.map((relatedVideo) => (
                        <Link
                          key={relatedVideo.id}
                          to={`/videos/${relatedVideo.slug}`}
                          className="block group"
                        >
                          <div className="flex gap-3">
                            <div className="relative w-20 h-14 rounded-lg overflow-hidden flex-shrink-0">
                              <img
                                src={relatedVideo.thumbnail}
                                alt={relatedVideo.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                              />
                              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                <Play className="w-5 h-5 text-white" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-brand-primary text-sm line-clamp-2 group-hover:text-brand-secondary transition-colors">
                                {relatedVideo.title}
                              </p>
                              <p className="text-xs text-slate-500 mt-1">{relatedVideo.duration}</p>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
